import React from 'react';
import { distributorsHeroData, distributorsStatsData } from '../../data/distributorsData';
import { Button } from '../ui/button';
import { ArrowRight, Play, Building2, TrendingUp } from 'lucide-react';

const DistributorsHero = () => {
  return (
    <section className="relative min-h-screen overflow-hidden bg-dark-ellipse">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
                           radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      {/* Floating Shapes */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#8bcaf9]/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[70vh]">
          {/* Left Content */}
          <div className="text-left">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-white/20">
              <Building2 className="w-4 h-4 text-white" />
              <span className="text-white text-sm font-medium">{distributorsHeroData.tagline}</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {distributorsHeroData.title}
            </h1>

            <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-xl leading-relaxed">
              {distributorsHeroData.subtitle}
            </p>

            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                className="bg-white hover:bg-[#e3e4e6] text-[#1297fd] font-semibold px-8 py-6 rounded-full text-lg transition-all hover:scale-105 hover:shadow-xl group"
              >
                {distributorsHeroData.cta}
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 rounded-full text-lg transition-all hover:scale-105 group bg-transparent"
              >
                <Play className="mr-2 w-5 h-5" />
                {distributorsHeroData.secondaryCta}
              </Button>
            </div>
          </div>

          {/* Right Content - Visual */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Main Card */}
              <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-1 shadow-2xl border border-white/20">
                <div className="bg-gradient-to-br from-gray-900/90 to-gray-800/90 rounded-2xl p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}>
                        <Building2 className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-white font-semibold">Portal Distribuidor</span>
                    </div>
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-[#8bcaf9]" />
                      <div className="w-3 h-3 rounded-full bg-green-400" />
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {[
                      { label: 'Resellers Activos', value: '1,247', change: '+18%' },
                      { label: 'Facturación Mensual', value: '$2.4M', change: '+24%' },
                      { label: 'Pedidos Procesados', value: '8,432', change: '+32%' },
                      { label: 'Vendors Integrados', value: '45', change: '+5' }
                    ].map((stat) => (
                      <div key={stat.label} className="bg-white/5 rounded-xl p-4">
                        <p className="text-[#8bcaf9] text-xs mb-1">{stat.label}</p>
                        <p className="text-white font-bold text-lg">{stat.value}</p>
                        <span className="text-green-400 text-xs">{stat.change}</span>
                      </div>
                    ))}
                  </div>

                  {/* Revenue Chart */}
                  <div className="bg-white/5 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-white text-sm font-medium">Ingresos por Canal</span>
                      <span className="text-green-400 text-sm">+25% YoY</span>
                    </div>
                    <div className="flex items-end gap-2 h-20">
                      {[35, 45, 40, 60, 55, 75, 70, 85, 80, 95, 90, 100].map((height, idx) => (
                        <div
                          key={idx}
                          className="flex-1 rounded-t-lg transition-all hover:opacity-80"
                          style={{ 
                            height: `${height}%`,
                            background: `linear-gradient(to top, #4E95D9, #2B6FA8)`
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Cards */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl transform rotate-3 hover:rotate-0 transition-transform">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="text-gray-900 font-bold text-sm">Crecimiento</p>
                    <p className="text-green-500 text-xs font-medium">+70% eficiencia</p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-xl transform -rotate-3 hover:rotate-0 transition-transform">
                <p className="text-[#1297fd] font-bold text-2xl">99.98%</p>
                <p className="text-[#c3c3c5] text-sm">Precisión</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {distributorsStatsData.map((stat, idx) => (
            <div
              key={idx}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10 text-center hover:bg-white/15 transition-all hover:scale-105 hover:border-white/30"
            >
              <p className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.number}</p>
              <p className="text-white/70 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Wave Divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
};

export default DistributorsHero;
